import {
  selectRemotePeers,
  selectRoom,
  useHMSStore,
} from "@100mslive/react-sdk";
import { Users } from "lucide-react";
import { PeerAvatar } from "@/components/common/PeerAvatar";
import { isInternalRole } from "@/lib/roles";

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/** "Ana Torres", "Ana Torres and Laura Méndez", "Ana Torres and 2 others". */
function namesOf(peers) {
  const [first, second] = peers.map(peer => peer.name || "Someone");
  if (peers.length === 1) {
    return first;
  }
  if (peers.length === 2) {
    return `${first} and ${second}`;
  }
  return `${first} and ${peers.length - 1} others`;
}

/**
 * Who is already in the call, under the pre-join title, like Cal Video's
 * "2 people in call". 100ms sends the room's head count during the preview
 * every few seconds (100ms leaves its recorder out of it); names come only
 * when the template sends its peer list, where 100ms's own roles are dropped.
 */
export const WhoIsHere = () => {
  const room = useHMSStore(selectRoom);
  const peers = (useHMSStore(selectRemotePeers) || []).filter(
    peer => !isInternalRole(peer.roleName)
  );
  const count = room?.peerCount;

  if (peers.length) {
    return (
      <p className="flex items-center gap-2 text-sm" data-testid="who_is_here">
        <span className="flex -space-x-1.5">
          {peers.slice(0, 2).map(peer => (
            <PeerAvatar
              key={peer.id}
              name={peer.name}
              className="size-5 text-[9px] ring-2 ring-card"
            />
          ))}
        </span>
        <span className="min-w-0 truncate">
          {namesOf(peers)} {peers.length === 1 ? "is" : "are"} in the call
        </span>
      </p>
    );
  }

  if (count === undefined) {
    return (
      <p className="text-sm text-muted-foreground">
        Check your camera and microphone first.
      </p>
    );
  }

  if (!count) {
    return (
      <p
        className="flex items-center gap-2 text-sm text-muted-foreground"
        data-testid="who_is_here"
      >
        <Users className="size-4" aria-hidden="true" />
        No one else is here yet
      </p>
    );
  }
  return (
    <p className="flex items-center gap-2 text-sm" data-testid="who_is_here">
      <span className="relative flex size-2">
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-500 opacity-60 motion-reduce:animate-none" />
        <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
      </span>
      {plural(count, "person is", "people are")} in the call
    </p>
  );
};
